/**
 * The HQ dashboard (M1.1, docs/11-interface.md): the M0 egui shell's economy
 * screens — firm vitals, the fab blocks (now *lines*), products and the launch
 * form, the quarterly P&L, rivals and events — ported as one overlay over the
 * live floor. Everything shown is read from `CampaignUi`; everything done is a
 * `Command` through the host's queue. Nothing here derives a sim quantity:
 * the store already carries the share, the yield, the cost per die, and the
 * figure trees the tooltips open into (decision 5 — legibility is
 * infrastructure). Where a number has no decomposition yet, it still gets an
 * `atomic` figure with a sentence saying what it is, so a player is never
 * looking at a bare integer.
 */

import { useState, useSyncExternalStore } from 'react';
import type { AssignmentUi, CampaignUi, EventUi, FirmUi, FloorHost, LineUi, ProductUi, RivalUi } from '../bridge';
import { d0OfMilli, int, money, pctOfMille, pctOfPpm, timesOfMille } from './format';
import { openHandbook, setPanels, usePanels } from './panels';
import { snapshot, subscribe } from './store';
import { Tip, atomic } from './Tooltip';

export function Dashboard({ host }: { host: FloorHost }) {
  const hud = useSyncExternalStore(subscribe, snapshot);
  const panels = usePanels();
  const c = hud.campaign;
  if (panels.economy !== 'dashboard' || !c) return null;
  const firm = c.firm;
  return (
    <div className="overlay dashboard">
      <header>
        <h2>Dashboard</h2>
        <span className="muted">
          {firm.name} · {firm.region} · {c.date}
        </span>
        <button className="help" title="the handbook: the ledger" onClick={() => openHandbook('ledger')}>
          ?
        </button>
        <button className="close" title="close" onClick={() => setPanels({ economy: null })}>
          ×
        </button>
      </header>

      <Vitals c={c} firm={firm} />
      <Lines c={c} firm={firm} host={host} />
      <Products c={c} firm={firm} host={host} />
      <Pnl firm={firm} />
      <Rivals rivals={c.rivals} />
      <Events c={c} />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Vital signs
 * ------------------------------------------------------------------ */

function Vitals({ c, firm }: { c: CampaignUi; firm: FirmUi }) {
  const runLabel =
    c.run === 'running' ? 'running' : c.run === 'ended:insolvency' ? 'ended — insolvent' : 'ended — the window closed';
  const runClass = c.run === 'running' ? 'ok' : 'over';
  const swingClass = firm.ledger.swing < 0 ? 'over' : firm.ledger.swing > 0 ? 'ok' : '';
  return (
    <div className="vitals">
      <span className="vital">
        <span className="vital-label">cash</span>
        <Tip figure={firm.cashFigure}>
          <span className={`vital-value ${firm.cash < 0 ? 'over' : ''}`}>{firm.cashFigure.value}</span>
        </Tip>
      </span>
      <span className="vital">
        <span className="vital-label">debt</span>
        <Tip
          figure={atomic(
            'Debt',
            money(firm.debt),
            'What has been drawn on the credit line. Interest on it is booked every week; it is repaid from cash as cash allows.',
          )}
        >
          <span className="vital-value">{money(firm.debt)}</span>
        </Tip>
        <span className="muted"> / </span>
        <Tip
          figure={atomic(
            'Credit limit',
            money(firm.creditLimit),
            'How far the bank lets cash go below zero before the run ends in insolvency.',
          )}
        >
          <span className="vital-value">{money(firm.creditLimit)}</span>
        </Tip>
      </span>
      <span className="vital">
        <span className="vital-label">capability</span>
        <Tip
          figure={atomic(
            'Process capability',
            firm.capability ?? '—',
            'The best node your firm can build to. Research or licensing moves it one rung; a fab block builds at it, and a design targets it or any rung below.',
          )}
        >
          <span className="vital-value">{firm.capability ?? '—'}</span>
        </Tip>
      </span>
      <span className="vital">
        <span className="vital-label">run</span>
        <span className={`vital-value ${runClass}`}>{runLabel}</span>
        <span className="muted">
          {' '}
          wk {c.week}/{c.windowWeeks}
        </span>
      </span>
      <span className="vital">
        <span className="vital-label">this week's swing</span>
        <Tip figure={firm.ledger.figure}>
          <span className={`vital-value ${swingClass}`}>{money(firm.ledger.swing)}</span>
        </Tip>
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Lines
 * ------------------------------------------------------------------ */

function Lines({ c, firm, host }: { c: CampaignUi; firm: FirmUi; host: FloorHost }) {
  const setStarts = (line: LineUi, slot: number, lots: number) => {
    const max = Math.max(line.curveTopStarts + 2, 8);
    const clamped = Math.max(0, Math.min(max, Math.round(lots)));
    host.sendSim({ SetLineStarts: { firm: firm.id, line: line.index, lots_per_day: clamped, slot } });
  };
  const assign = (line: LineUi, slot: number, product: string | null) => {
    host.sendSim({ AssignFab: { firm: firm.id, fab: line.index, product, slot } });
  };
  const curved = firm.lines.filter((l) => l.curve.length > 0);
  return (
    <section>
      <h3>Lines</h3>
      {firm.lines.length === 0 ? (
        <div className="hint">No capacity. Build a fab block from the Technology overlay.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>line</th>
              <th>node</th>
              <th>status</th>
              <th title="each line carries up to two products, each with its own release dial (M1.3)">products · dials</th>
              <th title="parametric quality per product: capability × experience, and the grade split it earns">quality</th>
              <th title="wafers started → out this week">started → out</th>
              <th>good dies</th>
              <th>yield</th>
              <th>D₀</th>
              <th>cost/wk</th>
              <th>$/good die</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {firm.lines.map((line) => {
              const isSite = c.siteLine != null && c.siteLine === line.index;
              const stale = c.siteCurveStale && (isSite || line.source === 'measured');
              const status =
                line.status === 'operating' ? 'operating' : `${line.status} — ${line.weeksLeft} wk left`;
              const kindHint =
                line.source === 'abstract'
                  ? 'An abstract fab block: M0-style capacity standing somewhere else, run as numbers. Not the floor below.'
                  : line.source === 'rented'
                    ? "Someone else's fab, hired by the wafer — fixed, mediocre, nobody here can improve it."
                    : 'The inhabited floor below, measured into its curve by the site sim.';
              return (
                <tr key={line.id}>
                  <td title={kindHint}>
                    {isSite ? (
                      <button className="link" onClick={() => setPanels({ economy: 'line' })} title="open the fab's own panel: readings, the last move's delta, the levers">
                        {line.name}
                      </button>
                    ) : (
                      line.name
                    )}
                    {line.bookedObserved && (
                      <span className="chip ok" title="last week's output was booked from the watched floor, not the curve">
                        watched
                      </span>
                    )}
                    {stale && (
                      <span className="chip warn" title="the floor changed since this curve was measured">
                        curve stale — the floor changed and has not re-measured
                      </span>
                    )}
                    {!line.hasCurve && (
                      <span className="chip warn" title="a line with no curve has no answer and ships nothing">
                        no curve
                      </span>
                    )}
                  </td>
                  <td>{line.nodeName}</td>
                  <td className={line.status === 'operating' ? '' : 'muted'}>{status}</td>
                  <td>
                    {line.assignments.map((a) => (
                      <div key={a.slot} className="assignment-row">
                        <select
                          value={a.product}
                          onChange={(e) => assign(line, a.slot, e.target.value === '' ? null : e.target.value)}
                        >
                          <option value="">— (clear slot)</option>
                          {firm.products
                            .filter((p) => p.node === line.node)
                            .map((p) => (
                              <option key={p.id} value={p.id} title={p.name}>
                                {p.id}
                              </option>
                            ))}
                        </select>{' '}
                        {line.canDial ? (
                          <span className="dial">
                            <button className="small" onClick={() => setStarts(line, a.slot, a.startsPerDay - 1)} disabled={a.startsPerDay <= 0}>
                              −
                            </button>
                            <Tip
                              figure={atomic(
                                'Release dial',
                                `${a.startsPerDay} lots/day`,
                                `Lot releases a day of ${a.product} on this line. The line's curve tops out at ${line.curveTopStarts} in total; past it there is nothing steady to predict.`,
                              )}
                            >
                              <span className="dial-value">{a.startsPerDay}</span>
                            </Tip>
                            <button className="small" onClick={() => setStarts(line, a.slot, a.startsPerDay + 1)}>
                              +
                            </button>
                          </span>
                        ) : (
                          <Tip
                            figure={atomic(
                              'Release dial',
                              `${a.startsPerDay} lots/day`,
                              line.source === 'measured'
                                ? "Follows the floor's release slot — set it on the fab panel, not here."
                                : 'A fab block runs at its authored capacity; there is no dial on an abstract block.',
                            )}
                          >
                            <span className="dial-value">{a.startsPerDay}</span>
                          </Tip>
                        )}
                      </div>
                    ))}
                    {line.assignments.length === 0 && (
                      <select value="" onChange={(e) => e.target.value !== '' && assign(line, 0, e.target.value)}>
                        <option value="">— (idle)</option>
                        {firm.products
                          .filter((p) => p.node === line.node)
                          .map((p) => (
                            <option key={p.id} value={p.id} title={p.name}>
                              {p.id}
                            </option>
                          ))}
                      </select>
                    )}
                    {line.canAssignSecond && (
                      <select
                        value=""
                        title="put a second product on this line — it gets its own release dial, and starts at the bottom of the experience ramp"
                        onChange={(e) => e.target.value !== '' && assign(line, line.assignments.length, e.target.value)}
                      >
                        <option value="">+ second product…</option>
                        {firm.products
                          .filter((p) => p.node === line.node && !line.assignments.some((a) => a.product === p.id))
                          .map((p) => (
                            <option key={p.id} value={p.id} title={p.name}>
                              {p.id}
                            </option>
                          ))}
                      </select>
                    )}
                  </td>
                  <td>
                    {line.assignments.length === 0 ? (
                      '—'
                    ) : (
                      line.assignments.map((a) => (
                        <QualityCell key={a.slot} a={a} named={line.assignments.length > 1} />
                      ))
                    )}
                  </td>
                  <td>
                    {int(line.wafersStarted)} → {int(line.wafersOut)}
                    {line.wipWafers > 0 && <span className="muted"> · {int(line.wipWafers)} WIP</span>}
                  </td>
                  <td>
                    {int(line.goodDies)}
                    {line.salvageDies > 0 && <span className="muted"> +{int(line.salvageDies)} salvage</span>}
                  </td>
                  <td>
                    <Tip
                      figure={atomic(
                        'Yield',
                        pctOfPpm(line.yieldPpm),
                        "Good dies over dies printed, this week's expected report at the current dial: node D₀ on its learning curve × the line's D₀ modifier, through the die area of the assigned product.",
                      )}
                    >
                      {line.product ? pctOfPpm(line.yieldPpm) : '—'}
                    </Tip>
                  </td>
                  <td>
                    <Tip
                      figure={atomic(
                        'D₀',
                        d0OfMilli(line.d0Milli),
                        `Defect density on this line: the node's learning-curve D₀ times the line's own modifier (${timesOfMille(line.lineD0Mille)} — cleanliness, discipline, fumbled loads).`,
                      )}
                    >
                      {line.product ? d0OfMilli(line.d0Milli) : '—'}
                    </Tip>
                  </td>
                  <td>
                    <Tip figure={line.costFigure}>{money(line.weeklyCost)}</Tip>
                  </td>
                  <td>
                    <Tip
                      figure={atomic(
                        'Cost per good die',
                        line.costPerGoodDie == null ? '—' : money(line.costPerGoodDie),
                        "This week's line cost over this week's good dies. No good dies, no number.",
                      )}
                    >
                      {line.costPerGoodDie == null ? '—' : money(line.costPerGoodDie)}
                    </Tip>
                  </td>
                  <td>
                    {line.canRetrofit && (
                      <button
                        className="small"
                        title={`retrofit this block to the next node (${money(line.retrofitCapex)})`}
                        onClick={() => host.sendSim({ RetrofitFab: { firm: firm.id, fab: line.index } })}
                      >
                        retrofit {money(line.retrofitCapex)}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {curved.length > 0 && (
        <div className="curves">
          {curved.map((line) => (
            <div key={line.id} className="curve-row">
              <span className="muted">{line.name} curve · </span>
              {line.curve.map((p, i) => (
                <span key={i} className={`curve-point ${p.current ? 'current' : ''}`}>
                  {p.startsPerDay}→{p.wafersOutPerWeek}/wk ({p.cycleHours.toFixed(0)} h, D₀ {timesOfMille(p.d0Mille)})
                </span>
              ))}
              <span className="muted"> · capacity {int(line.capacityWafersPerWeek)} wafers/wk</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** One product's quality readout on one line, as one number: the parametric
 * quality percentage. The tooltip's figure carries the decomposition —
 * capability × experience, and the grade split it earns (M1.3). */
function QualityCell({ a, named }: { a: AssignmentUi; named: boolean }) {
  return (
    <div className="assignment-row">
      {named && <span className="muted">{a.product} </span>}
      <Tip figure={a.qualityFigure}>
        <span className="dial-value">{(a.qualityMille / 10).toFixed(0)}%</span>
      </Tip>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Products and the launch form
 * ------------------------------------------------------------------ */

function Products({ c, firm, host }: { c: CampaignUi; firm: FirmUi; host: FloorHost }) {
  // Economy commands apply on arrival (M1.5's input rework), but the UI
  // store republishes on its own ~100 ms cadence — so a rapid second click
  // could compute from a value the sim has already moved past. This is the
  // optimistic echo over that window: remember the ask you ordered, render
  // it beside the dial until the store reflects it, and compute further
  // clicks from it so they stack instead of re-sending the same step. The
  // sim's own value takes over the moment it catches up.
  const [ordered, setOrdered] = useState<Record<string, { week: number; value: number }>>({});
  const pendingOf = (key: string, simValue: number): number | null => {
    const entry = ordered[key];
    if (!entry || c.week > entry.week || entry.value === simValue) return null;
    return entry.value;
  };
  const place = (key: string, value: number) => {
    setOrdered((prev) => {
      const next: typeof prev = {};
      for (const [k, e] of Object.entries(prev)) {
        if (c.week <= e.week) next[k] = e; // prune what past weeks settled
      }
      next[key] = { week: c.week, value };
      return next;
    });
  };
  const setPrice = (p: ProductUi, factorMille: number) => {
    const base = pendingOf(p.id, p.priceMille) ?? p.priceMille;
    const price = Math.max(100, Math.min(10_000, Math.round((base * factorMille) / 1000)));
    place(p.id, price);
    host.sendSim({ SetPrice: { firm: firm.id, product: p.id, price_mille: price } });
  };
  const setGradePrice = (p: ProductUi, grade: number, askMille: number) => {
    const price = Math.max(100, Math.min(2000, Math.round(askMille)));
    place(`${p.id}/${grade}`, price);
    host.sendSim({ SetGradePrice: { firm: firm.id, product: p.id, grade, price_mille: price } });
  };
  return (
    <section>
      <h3>Products</h3>
      {firm.products.length === 0 ? (
        <div className="hint">No designs.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>design</th>
              <th>category</th>
              <th>node</th>
              <th>die</th>
              <th title="performance vs the ambient reference">eff perf</th>
              <th title="price as a multiple of the category's spot index">price</th>
              <th>asking</th>
              <th>lines</th>
              <th>sold / unsold</th>
              <th>revenue</th>
              <th>yield</th>
              <th title="the bin ladder: each grade's ask relative to the flagship price (M1.3)">grades</th>
            </tr>
          </thead>
          <tbody>
            {firm.products.map((p) => (
              <tr key={p.id}>
                <td>
                  <span title={p.name}>{p.id}</span> <span className="muted">{p.name}</span>
                </td>
                <td>{p.categoryName}</td>
                <td>{p.nodeName}</td>
                <td>{p.dieMm2} mm²</td>
                <td>
                  <Tip figure={p.compositeFigure}>
                    {timesOfMille(p.compositeVsAmbientMille)}
                  </Tip>
                  {p.stockUnits > 0 && (
                    <Tip
                      figure={atomic(
                        'In stock',
                        `${int(p.stockUnits)} units`,
                        'Finished, unsold, in the warehouse — offered again every week at your asks, carried at cost (M1.4).',
                      )}
                    >
                      <span className="muted"> · {int(p.stockUnits)} stocked</span>
                    </Tip>
                  )}
                </td>
                <td>
                  <span className="dial">
                    <button className="small" onClick={() => setPrice(p, 900)} title="−10%">
                      −10%
                    </button>
                    <span className="dial-value">
                      {timesOfMille(p.priceMille)} idx
                      {pendingOf(p.id, p.priceMille) != null && (
                        <span className="muted" title="ordered — applies when the week closes">
                          {' '}
                          → {timesOfMille(pendingOf(p.id, p.priceMille)!)}
                        </span>
                      )}
                    </span>
                    <button className="small" onClick={() => setPrice(p, 1100)} title="+10%">
                      +10%
                    </button>
                  </span>
                </td>
                <td>
                  <Tip
                    figure={atomic(
                      'Asking price',
                      money(p.askingPrice),
                      `The category's spot index × your price multiple (${timesOfMille(p.priceMille)}). What a unit fetches this week if it sells.`,
                    )}
                  >
                    {money(p.askingPrice)}
                  </Tip>
                </td>
                <td>{p.linesRunning}</td>
                <td>
                  {int(p.lastSold)} / <span className={p.lastUnsold > 0 ? 'warn' : ''}>{int(p.lastUnsold)}</span>
                </td>
                <td>{money(p.lastRevenue)}</td>
                <td>
                  <Tip
                    figure={atomic(
                      'Yield preview',
                      pctOfPpm(p.yieldPpm),
                      `What this die area yields on ${p.nodeName} at today's learning state — ${p.diesPerWafer} dies a wafer before yield.`,
                    )}
                  >
                    {pctOfPpm(p.yieldPpm)}
                  </Tip>
                </td>
                <td>
                  {p.grades.length <= 1
                    ? '—'
                    : p.grades.map((g) => (
                        <div key={g.id} className="assignment-row">
                          <Tip
                            figure={atomic(
                              `${g.name}`,
                              `${timesOfMille(g.askMille)} of flagship`,
                              `A rated variant of ${p.id}: sold at ${(g.perfMille / 10).toFixed(0)}% of the design's performance. Its ask is a share of the flagship price — a down-bin priced too near the flagship eats the flagship's share.`,
                            )}
                          >
                            <span className="muted">{g.name}</span>
                          </Tip>{' '}
                          <span className="dial">
                            <button
                              className="small"
                              onClick={() =>
                                setGradePrice(p, g.index, (pendingOf(`${p.id}/${g.index}`, g.askMille) ?? g.askMille) - 50)
                              }
                              title="−5%"
                            >
                              −
                            </button>
                            <span className="dial-value">
                              {timesOfMille(g.askMille)}
                              {pendingOf(`${p.id}/${g.index}`, g.askMille) != null && (
                                <span className="muted" title="ordered — applies when the week closes">
                                  {' '}
                                  → {timesOfMille(pendingOf(`${p.id}/${g.index}`, g.askMille)!)}
                                </span>
                              )}
                            </span>
                            <button
                              className="small"
                              onClick={() =>
                                setGradePrice(p, g.index, (pendingOf(`${p.id}/${g.index}`, g.askMille) ?? g.askMille) + 50)
                              }
                              title="+5%"
                            >
                              +
                            </button>
                          </span>
                        </div>
                      ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <LaunchForm c={c} firm={firm} host={host} />
    </section>
  );
}

function LaunchForm({ c, firm, host }: { c: CampaignUi; firm: FirmUi; host: FloorHost }) {
  const attained = c.tech.nodes.filter((n) => n.attained);
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [node, setNode] = useState('');
  const [die, setDie] = useState(20);
  const [perf, setPerf] = useState(1000);
  if (!firm.capability || attained.length === 0) {
    return <div className="hint">No process capability — nothing to design for.</div>;
  }
  // The egui shell's defaults: the newest node you can build to, the first
  // category, a 20 mm² die at the 1.0× performance point.
  const defaultId = `p${firm.products.length + 1}`;
  const effCategory = c.markets.some((m) => m.id === category) ? category : (c.markets[0]?.id ?? '');
  const effNode = attained.some((n) => n.id === node) ? node : (attained[attained.length - 1]?.id ?? '');
  const effId = id.trim() || defaultId;
  const categoryName = c.markets.find((m) => m.id === effCategory)?.name ?? effCategory;
  const nodeName = attained.find((n) => n.id === effNode)?.name ?? effNode;
  const effName = name.trim() || `${effId} ${categoryName} ${die} mm² (${nodeName})`;
  const taken = firm.products.some((p) => p.id === effId);
  const launch = () => {
    host.sendSim({
      LaunchProduct: {
        firm: firm.id,
        id: effId,
        name: effName,
        category: effCategory,
        node: effNode,
        die_mm2: Math.max(1, Math.min(600, Math.round(die))),
        perf_mille: Math.max(100, Math.min(10_000, Math.round(perf))),
        // Until the Studio computes the vector (M1.5/M1.6), a hand-launched
        // design reads its perf point on every axis.
        attrs_mille: [],
      },
    });
    setId('');
    setName('');
  };
  return (
    <div className="launch">
      <h3>Launch a design</h3>
      <div className="form-row">
        <label>
          id <input type="text" value={id} placeholder={defaultId} onChange={(e) => setId(e.target.value)} />
        </label>
        <label>
          name <input type="text" value={name} placeholder={effName} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          category{' '}
          <select value={effCategory} onChange={(e) => setCategory(e.target.value)}>
            {c.markets.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          node{' '}
          <select value={effNode} onChange={(e) => setNode(e.target.value)}>
            {attained.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          die mm² <input type="number" min={1} max={600} value={die} onChange={(e) => setDie(Number(e.target.value))} />
        </label>
        <label>
          perf ‰{' '}
          <input type="number" min={100} max={10_000} step={100} value={perf} onChange={(e) => setPerf(Number(e.target.value))} />
        </label>
        <button onClick={launch} disabled={taken || c.run !== 'running'} title={taken ? `a design with id ${effId} exists` : 'launch'}>
          Launch
        </button>
      </div>
      <div className="hint">
        A design is category × node × die area × performance point. Applied on arrival; the yield preview
        appears in the table once it exists.
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * P&L
 * ------------------------------------------------------------------ */

function Pnl({ firm }: { firm: FirmUi }) {
  const rows = [firm.quarter, ...firm.quarters];
  return (
    <section>
      <h3>P&amp;L — quarterly</h3>
      <table>
        <thead>
          <tr>
            <th>quarter</th>
            <th>revenue</th>
            <th>opex</th>
            <th>R&amp;D</th>
            <th>interest</th>
            <th>net</th>
            <th>capex</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((q, i) => (
            <tr key={`${q.label}-${i}`}>
              <td>{q.label}</td>
              <td>{money(q.revenue)}</td>
              <td>{money(q.operatingExpenses)}</td>
              <td>{money(q.rnd)}</td>
              <td>{money(q.interest)}</td>
              <td className={q.net < 0 ? 'over' : q.net > 0 ? 'ok' : ''}>{money(q.net)}</td>
              <td>{money(q.capex)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Rivals
 * ------------------------------------------------------------------ */

function archetypeLabel(a: string): string {
  // `TechLeader` → `tech leader`: formatting the enum's debug name, nothing more.
  return a.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}

function Rivals({ rivals }: { rivals: RivalUi[] }) {
  return (
    <section>
      <h3>Rivals</h3>
      {rivals.length === 0 ? (
        <div className="hint">No rivals in this world.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>rival</th>
              <th>archetype</th>
              <th>region</th>
              <th>node</th>
              <th title="blocks operating / total (ceiling)">blocks</th>
              <th>project</th>
              <th>ask</th>
              <th>cash</th>
              <th>DRAM share</th>
            </tr>
          </thead>
          <tbody>
            {rivals.map((r) => (
              <tr key={r.id} className={r.alive ? '' : 'dead'}>
                <td>
                  {r.alive ? '' : '† '}
                  {r.name}
                  {r.consortium && <span className="chip warn">{r.consortium}</span>}
                </td>
                <td>{archetypeLabel(r.archetype)}</td>
                <td>{r.region}</td>
                <td>{r.capability ?? '—'}</td>
                <td>
                  {r.blocksOperating}/{r.blocksTotal} <span className="muted">(max {r.maxBlocks})</span>
                </td>
                <td>{r.project ?? '—'}</td>
                <td>{timesOfMille(r.priceMille)} idx</td>
                <td>{money(r.cash)}</td>
                <td>
                  <Tip
                    figure={atomic(
                      'DRAM share',
                      pctOfMille(r.dramShareMille),
                      "This rival's DRAM units sold last week over the category's demand.",
                    )}
                  >
                    {pctOfMille(r.dramShareMille)}
                  </Tip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Events
 * ------------------------------------------------------------------ */

function eventStatus(e: EventUi): string {
  const fired = e.firedWeek < 0 ? `${-e.firedWeek} wk pre-seed` : `wk ${e.firedWeek}`;
  if (e.status === 'active') return `ACTIVE — ${e.weeksLeft} wk left (since ${fired})`;
  if (e.status === 'done') return `fired ${fired}`;
  return e.ripe ? 'ripe — may fire' : 'latent';
}

function Events({ c }: { c: CampaignUi }) {
  return (
    <section>
      <h3>Events</h3>
      <div className="hint">
        Gravity of History{' '}
        <Tip
          figure={atomic(
            'Gravity of History',
            pctOfMille(c.gravityMille, 0),
            'Biases *when* ripe events fire toward their historical anchor; never a date trigger.',
          )}
        >
          {pctOfMille(c.gravityMille, 0)}
        </Tip>
        {' · '}
        macro overlay on demand{' '}
        <Tip
          figure={atomic(
            'Demand multiplier',
            pctOfMille(c.demandMille, 0),
            "This week's macro overlay on every category's demand, from the events active now. 100% means no event is pressing.",
          )}
        >
          <span className={c.demandMille === 1000 ? '' : 'warn'}>{pctOfMille(c.demandMille, 0)}</span>
        </Tip>
      </div>
      {c.events.length === 0 ? (
        <div className="hint">No authored events in this world.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>event</th>
              <th>template</th>
              <th>target</th>
              <th>magnitude</th>
              <th>anchor</th>
              <th>status</th>
            </tr>
          </thead>
          <tbody>
            {c.events.map((e) => (
              <tr key={e.id} className={e.status === 'active' ? 'me' : ''}>
                <td>{e.id}</td>
                <td>{e.template}</td>
                <td>{e.category ?? 'industry'}</td>
                <td>{timesOfMille(e.magnitudeMille)}</td>
                <td>wk {e.anchorWeek}</td>
                <td className={e.status === 'pending' && e.ripe ? 'warn' : ''}>{eventStatus(e)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
